/* globals PROD_ENV, NODE_ENV, FACEBOOK_PIXEL_ID, GOOGLE_ANALYTICS_ID, dataLayer */
import ReactGA from 'react-ga4'
import ReactPixel from 'react-facebook-pixel'
import { sha256 } from 'js-sha256'
import logger from 'components/utils/logger'
import {
  getUserAddresses,
  getUserPaymentMethods,
  types as userTypes,
  savePaymentMethod,
  updateUserProfile,
  changePlan
} from 'reduxModules/users'
import { initConfirmation } from 'reduxModules/confirmation'
import { cartItemsFetch, types as cartTypes } from 'reduxModules/cart'
import getCountryCode from 'reduxModules/utils/getCountryCode'
import {
  getAdDataObjectFrom,
  getUTMDataFromCookies
} from 'reduxModules/utils/utm'
import { showNewPaymentMethodForm } from 'reduxModules/forms/paymentMethod'
import {
  dispatchGetUserBraintreeToken,
  braintreeErrorHandler
} from 'reduxModules/users/dispatchers'
import _ from 'lodash'
import { bannerHandler, innerNavHandlers } from 'reduxModules/app/dispatchers'
import {
  toggleDiscountCode,
  validateDiscountCode,
  types as discountCodeTypes,
  clearDiscountCode,
  saveValidatedCode,
  rejectDiscountCode
} from 'reduxModules/discountCode'

import googleTrackConversion from 'components/utils/googleConversion'
import addFraudProtection from 'components/utils/addFraudProtection'
import {
  getGlobalPromoCookie,
  promoDiscountForPlan
} from 'reduxModules/utils/promo'
import {
  getUser,
  loginFromEmail as loginFromEmailAction
} from 'reduxModules/auth'
import { scroller } from 'react-scroll'
import {
  isSubscriptionPending,
  markSubscriptionPending,
  types as subscriptionTypes,
  subscriptionPlansFetch
} from 'reduxModules/subscriptions'
import { clearRecaptchaToken } from 'reduxModules/app'
import getErrorMessage from 'reduxModules/utils/getErrorMessage'
import { navigate } from '../../../utils/navigation'
import { captureGa4Identifiers } from '../../../utils/ga4Identifiers'
import pushGa4Event from '../../../utils/pushGa4Event'
import {
  useNewAddress,
  useExistentAddress as useExistentAddressAction,
  chooseAddress,
  validateAddress,
  saveDeliveryMethod,
  saveNewAddress,
  cancelNewAddress,
  completeShippingInfo,
  selectPaymentMethod,
  toggleBillToShippingAddressAction,
  useExistentPaymentMethod as useExistentPaymentMethodAction,
  useNewPaymentMethod,
  settleOrder,
  saveBillingAddress,
  initializeShippingInfoAction,
  types,
  clearOrderAction,
  clearSelectedPayment,
  useNewBillingAddress,
  useExistentBillingAddress,
  useNewlySavedPaymentMethod,
  subscribe,
  removeCheckoutCloak,
  cloakCheckout,
  selectPayWithExistentPaypal,
  selectPayWithCreditCard,
  activateSubscribingMode,
  enableCheckoutButton,
  disableCheckoutButton,
  fetchLifetimePreview as fetchLifetimePreviewAction,
  lifetimePreviewFetch,
  lifetimePreviewClear
} from './'
import {
  lifetimePlanFromListSelector,
  planSelectedSelector
} from './selectors'
import notifications, {
  toastError,
  toastFailure,
  toastServerError,
  toastSuccess
} from '../../../global/notifications'

const usedFreeTrialConfirmation = {
  title: 'Warning',
  text: 'We see that you’ve already used your free 7 day trial, If you continue, your subscription will activate immediately and you will be charged',
  options: [{ resolveValue: 'accept', text: 'Accept', class: 'confirm-btn' }]
}

const getCompleteAddress = (oldAddress) => ({
  ...oldAddress,
  countryCodeAlpha2: getCountryCode(oldAddress.countryName)
})

// SPEC-063 FR-6: a refusal here is the backend explaining itself ("This plan
// is not discountable.") — show it, drop the code, keep the reason for the
// field. fetchHelper never rejects on a 4xx, so there is nothing to catch.
const validateCoupon = (dispatch, notification, validatedCode, plan) => {
  if (!validatedCode.codename) return
  dispatch(validateDiscountCode(validatedCode.codename, plan)()).then(
    ([, res]) => {
      if (res.type === discountCodeTypes.VALIDATE_DISCOUNT_CODE_SUCCESS) return
      toastFailure(notification, res.payload)
      // the body, not the {value, status} envelope: fetchFailureMiddleware
      // treats any action carrying status 400 as a form failure and throws
      dispatch(rejectDiscountCode(res.payload?.value ?? res.payload))
    }
  )
}

// Resolves with true when a payment method is ready to charge, false when it
// failed — callers must not continue to the subscription/order submit on false,
// or the backend rejects it with a misleading "nonce may not be null" error.
const tokenizeCheckout = (
  dispatch,
  tokenizer,
  options,
  deviceData,
  recaptcha
) =>
  tokenizer()
    .then((payload) => {
      if (options.storeMethod)
        return dispatch(
          savePaymentMethod(recaptcha)({
            paymentMethodNonce: payload.nonce,
            deviceData
          })
        )
          .then(([_, res]) => {
            if (res.type === userTypes.USERS_PAYMENT_METHOD_SAVE_SUCCESS) {
              dispatch(useNewlySavedPaymentMethod(res.payload))
              return true
            }
            // Surface the API's reason (declined card, CVV mismatch...)
            // instead of silently discarding it
            toastFailure(notifications.paymentMethodCreated, res.payload)
            dispatch(enableCheckoutButton())
            return false
          })
          .catch((error) => {
            toastServerError(error.errors)
            dispatch(enableCheckoutButton())
            return false
          })
      dispatch(selectPaymentMethod(payload))
      return true
    })
    .catch((error) => {
      braintreeErrorHandler(dispatch, error)
      return false
    })

export const tokenize = (dispatch, tokenizer, options, deviceData, recaptcha) => {
  tokenizer()
    .then((payload) => {
      if (options.storeMethod)
        dispatch(
          savePaymentMethod(recaptcha)({
            paymentMethodNonce: payload.nonce,
            deviceData
          })
        ).then(([_, res]) => {
          if (res.type === userTypes.USERS_PAYMENT_METHOD_SAVE_SUCCESS) {
            dispatch(useNewlySavedPaymentMethod(res.payload))
            navigate('/review_order')
          } else
            // Same silent-failure shape as tokenizeCheckout: surface the
            // API's reason instead of dropping it
            toastFailure(notifications.paymentMethodCreated, res.payload)
        })
      else {
        dispatch(selectPaymentMethod(payload))
        navigate('/review_order')
      }
    })
    .catch((error) => braintreeErrorHandler(dispatch, error))
}

const dispatchClearCheckout = (dispatch) => ({
  clearCheckout() {
    dispatch(clearOrderAction())
    dispatch(clearDiscountCode())
  }
})

export const dispatchInitAddresses = (
  dispatch,
  stateProps,
  address = {},
  orderIsDigital
) => {
  let selectedAddress
  let completeAddress
  let payloadToValidate

  dispatch(getUserAddresses()).then((res) => {
    if (res[1].type === userTypes.USERS_ADDRESSES_FETCH_SUCCESS) {
      const addresses = res[1].payload.results
      let useExistentAddress = stateProps.useExistentAddress
      const currentBillingAddress = stateProps.selectedBillingAddress
      const previousDeliveryMethod = stateProps.selectedDeliveryMethod
      const deliveryMethods = stateProps.deliveryMethods
      const coupon = stateProps.validatedCode.codename

      if (!orderIsDigital) {
        if (_.isNumber(address)) {
          // user selected existent one
          selectedAddress = _.find(addresses, { id: address })
          useExistentAddress = true
        } else if (_.isObject(address))
          // user created a new one
          selectedAddress = address
        else if (!_.isEmpty(addresses)) {
          // no selection, pick the 1st one if able
          selectedAddress = addresses[0]
          useExistentAddress = true
        } // if no option available, nothing can be done
        else return

        if (useExistentAddress) dispatch(useExistentAddressAction())

        completeAddress = getCompleteAddress(selectedAddress)
        payloadToValidate = useExistentAddress
          ? { id: completeAddress.id }
          : completeAddress

        dispatch(validateAddress({ ...payloadToValidate, coupon }))
          .then((res) => {
            if (
              res[1].type === types.VALIDATE_ADDRESS_SUCCESS &&
              res[1].payload.deliveryMethods.length > 0
            ) {
              if (_.isEmpty(previousDeliveryMethod)) {
                const defaultMethod = res[1].payload.deliveryMethods[0]
                const code = defaultMethod.code
                const price = defaultMethod.cost

                dispatch(saveDeliveryMethod({ code, price }))
              } else {
                const method = _.find(deliveryMethods, {
                  code: previousDeliveryMethod.code
                })
                dispatch(
                  saveDeliveryMethod({ code: method.code, price: method.cost })
                )
              }

              if (!useExistentAddress)
                // if using a new address, save it
                dispatch(saveNewAddress(completeAddress))

              if (
                _.isEmpty(currentBillingAddress) && // if no billing address
                !_.isNumber(currentBillingAddress)
              )
                // use current as such
                dispatch(
                  saveBillingAddress(
                    useExistentAddress ? completeAddress.id : completeAddress
                  )
                )

              dispatch(
                chooseAddress(
                  useExistentAddress ? completeAddress.id : completeAddress
                )
              )

              dispatch(removeCheckoutCloak())
            }
          })
          .catch((payload) => toastServerError(payload.errors))
      } else if (
        !_.isNumber(currentBillingAddress) &&
        _.isEmpty(currentBillingAddress) &&
        !_.isEmpty(addresses)
      ) {
        dispatch(useExistentBillingAddress())
        dispatch(saveBillingAddress(addresses[0].id))
      }
    }
  })
}

export const resolveSuccessfulSettling = (dispatch, isSubscribing) => {
  if (!isSubscribing) navigate('/receipt')
  else navigate('/subscription/confirmation')

  // Delay clearing the order to ensure navigation completes first
  // This prevents React 18's batching from causing race conditions
  // where route guards (RequireOrderReady) see the cleared state before navigation completes
  // 100ms gives React Router time to unmount the ReviewOrder component
  setTimeout(() => {
    dispatch(clearOrderAction())
  }, 100)
  toastSuccess(
    isSubscribing ? notifications.subscribe : notifications.orderPlaced
  )
}

// Resolve the real `premium_lifetime` plan from state.subscriptions.plans (by name —
// rank collides with Group Learning), falling back to subscriptionPlansFetch() if the list
// hasn't loaded yet, then seed it as the active subscribing plan via activateSubscribingMode.
// Awaited by selectPlan so navigate('/checkout') only fires after planSelected.id is real.
export const seedLifetimeFromPlanList = () => async (dispatch, getState) => {
  let plan = lifetimePlanFromListSelector(getState())
  if (!plan) {
    await dispatch(subscriptionPlansFetch())
    plan = lifetimePlanFromListSelector(getState())
  }
  if (!plan) {
    toastServerError('Lifetime plan unavailable')
    return false
  }
  dispatch(activateSubscribingMode(plan))
  return true
}

// Hits GET /plans/<id>/?coupon=<code>. Manages loading/error in state.checkout.lifetimePreview.
export const fetchLifetimePreview = (planId, coupon) => async (dispatch) => {
  if (!planId || typeof planId !== 'number') return
  dispatch(lifetimePreviewFetch())
  try {
    await dispatch(fetchLifetimePreviewAction(planId, coupon)())
  } catch (_) {
    // failure action already dispatched by fetchApi; payload populates lifetimePreview.error
  }
}

// SPEC-059: validates with the single-plan `?plan=<id>` shape so the backend
// runs every plan-aware check (discountable, for_subscription, plan
// restriction) and refuses with a reason. The `?plans=[...]` array shape used
// before routes to the pricing-page serializer, which by design answers a
// mismatched plan with a zero discount instead of an error — correct on the
// pricing page, a silent no-op here. fetchHelper never rejects on a 4xx (it
// resolves with the failure tuple), so the outcome is read from the action
// type; the reducer keeps the refusal for the field to display.
export const applyLifetimeCoupon = (code) => async (dispatch, getState) => {
  const planSelected = planSelectedSelector(getState())
  if (!planSelected || !planSelected.id) return false
  const codename = String(code || '').trim().toUpperCase()
  const [, res] = await dispatch(
    validateDiscountCode(codename, planSelected.id)()
  )
  if (res.type !== discountCodeTypes.VALIDATE_DISCOUNT_CODE_SUCCESS)
    return false
  await dispatch(fetchLifetimePreview(planSelected.id, codename))
  const { lifetimePreview } = getState().checkout
  // FR-3: accepted by the validator but worth nothing on this purchase must
  // not read as success
  if (!(parseFloat(lifetimePreview.couponDiscount) > 0)) {
    dispatch(
      rejectDiscountCode('This code does not reduce the Lifetime price.')
    )
    await dispatch(fetchLifetimePreview(planSelected.id))
    return false
  }
  toastSuccess(notifications.discountCodeValid)
  return true
}

export const clearLifetimeCoupon = () => async (dispatch, getState) => {
  dispatch(lifetimePreviewClear())
  const planSelected = planSelectedSelector(getState())
  if (planSelected && planSelected.id)
    await dispatch(fetchLifetimePreview(planSelected.id))
}

// GA identifiers for a one-off purchase request, shaped exactly like the
// signup checkout's googleConversion (actuallySubscribe below) so the backend
// validates both with the same serializer. Resolves to undefined — the field
// is then left off the request — when the client id could not be captured,
// so the backend applies its own fallback rather than stitching to a wrong
// session. The capture is bounded (CAPTURE_TIMEOUT_MS) and this never throws.
export const captureGoogleConversionForPurchase = async () => {
  try {
    const { client_id: clientId, session_id: sessionId } =
      await captureGa4Identifiers()
    if (!clientId) return undefined
    return {
      clientId,
      userAgent: navigator.userAgent.substring(0, 256),
      ...(sessionId != null && { sessionId: String(sessionId) })
    }
  } catch (_) {
    return undefined
  }
}

// Submits the lifetime purchase via PATCH /subscriptions/change_plan/. On 200, awaits getUser
// (so auth.subscription.purchaseType is fresh before destination guards/copy run) then navigates
// to /subscription/confirmation?lifetime=1. On 409 already_lifetime, navigates to /account.
//
// payment_method_nonce is required by the backend for lifetime; we only attach the field
// when we actually have a value so we don't send `null` (which would still trigger the
// "A payment nonce is required for lifetime." 400). paymentMethodToken is sent only on
// the saved-method path; backend may or may not accept it depending on lifetime config.
export const purchaseLifetime = ({
  newPlan,
  paymentMethodNonce,
  paymentMethodToken,
  coupon,
  deviceData
}) => async (dispatch) => {
  if (!paymentMethodNonce && !paymentMethodToken) {
    toastServerError(
      'Please choose a payment method before completing the purchase.'
    )
    return { success: false, error: 'missing-payment-method' }
  }

  const request = { newPlan }
  if (paymentMethodNonce) request.paymentMethodNonce = paymentMethodNonce
  if (paymentMethodToken) request.paymentMethodToken = paymentMethodToken
  if (coupon) request.coupon = coupon
  if (deviceData) request.deviceData = deviceData
  // SPEC-068: the GA client/session ids let the backend's server-side
  // purchase event stitch to the buyer's session (same object, same rules
  // as the signup checkout: omitted entirely when capture fails, never
  // sent empty, and never allowed to block the purchase).
  const googleConversion = await captureGoogleConversionForPurchase()
  if (googleConversion) request.googleConversion = googleConversion

  try {
    const [, res] = await dispatch(changePlan(request))
    if (res && res.type === userTypes.USERS_CHANGE_PLAN_SUCCESS) {
      await dispatch(getUser())
      toastSuccess(notifications.oneTimePurchase)
      setTimeout(() => dispatch(clearOrderAction()), 100)
      navigate('/subscription/confirmation?lifetime=1', { replace: true })
      return { success: true }
    }
    if (res && res.payload && res.payload.status === 409) {
      await dispatch(getUser())
      navigate('/account?lifetimeOwned=1', { replace: true })
      return { success: false, alreadyLifetime: true }
    }
    // SPEC-059: a 4xx resolves here (fetchHelper never rejects on one), and
    // the backend's refusal — "This coupon does not apply to the Lifetime
    // plan.", a nonce problem — is the message the buyer needs to act on.
    const message = getErrorMessage(
      res && res.payload,
      notifications.oneTimePurchase.error.message
    )
    toastFailure(notifications.oneTimePurchase, res && res.payload)
    return { success: false, error: message }
  } catch (err) {
    const message = (err && err.message) || 'Lifetime purchase failed.'
    toastServerError(message)
    return { success: false, error: message }
  }
}

export const trackSubscribeSuccess = (res, userEmail) => {
  try {
    const subscription = res.payload
    googleTrackConversion('order', subscription.price)

    const pixelData = {
      content_name: subscription.plan,
      value: `${subscription.price}`,
      currency: 'USD',
      email: sha256(userEmail)
    }
    ReactPixel.trackCustom('SubscriptionConfirmation', pixelData)

    // GTM event — trial vs immediate charge (per Viden Oct 24 spec)
    // Actual purchase tracking is handled server-side via Measurement Protocol
    pushGa4Event(
      'GTMevent',
      {
        eventCategory: 'User Subscribes',
        eventAction: subscription?.onTrial ? 'Free Trial' : 'Direct Subscribe',
        eventLabel: subscription?.plan,
        free: subscription?.free,
        userId: subscription?.userId,
        subscriptionId: subscription?.id,
        discountType: subscription?.discountType || 'Regular',
        discountPercent: Number(subscription?.discountPercentage) || 0
      },
      subscription?.userId
    )
  } catch (e) {
    logger.error(`Error tracking subscription: ${e}`, {
      parameters: { res, userEmail }
    })
  }
}

function trackDVDPurchase(order) {
  const items = order?.items?.map((item) => ({
    id: item.id, // Must match transaction ID
    name: item?.ordered?.title || item?.ordered?.tutorial?.title,
    category: item?.orderedModel, // Category or product group
    price: String(order.cost), // Item price
    quantity: item?.quantity // Quantity
  }))
  pushGa4Event(
    'purchase', // or whatever event name your GTM setup expects
    {
      ecommerce: {
        transaction_id: order.id, // or any unique ID for the transaction
        affiliation: 'SWSCA Store',
        value: order.cost, // total cost
        currency: 'USD',
        items
      }
    },
    order?.userId
  )
}

export const invokeDispatchSubscription = (
  dispatch,
  dataSubscription,
  userEmail,
  googleConversion
) => {
  if (googleConversion === undefined || googleConversion.clientId === undefined)
    dataSubscription.googleConversion = undefined
  else dataSubscription.googleConversion = googleConversion

  dispatch(subscribe()({ ...dataSubscription }))
    .then(([_, res]) => {
      if (res.type === types.SUBSCRIBE_SUCCESS) {
        resolveSuccessfulSettling(dispatch, true)
        if (PROD_ENV) trackSubscribeSuccess(res, userEmail)
      } else {
        toastFailure(notifications.subscribe, res.payload)
        logger.error('Unsuccessful subscription attempt', {
          payload: res.payload,
          parameters: {
            invokeDispatchSubscription: {
              dispatch,
              dataSubscription,
              userEmail,
              googleConversion
            }
          }
        })
        dispatch(enableCheckoutButton())
      }
    })
    .catch((error) => {
      dispatch(enableCheckoutButton())
      toastError(notifications.orderPlaced)
      logger.error('Error caught when dispatching subscribe action', {
        error,
        parameters: {
          invokeDispatchSubscription: {
            dispatch,
            dataSubscription,
            userEmail,
            googleConversion
          }
        }
      })
    })
    .finally(() => dispatch(clearRecaptchaToken()))
}

export const actuallySubscribe = (
  dispatch,
  dataSubscription,
  userEmail,
  invokeDispatchSubscription
) => {
  try {
    // Capture via the shared helper (bounded timeout, applies fallbacks).
    // Per FR-5 / SPEC-026, only forward clientId when capture succeeded; when
    // it failed we omit it so the backend applies its own str(user.pk)
    // fallback and both sides agree on the same client_id.
    captureGa4Identifiers()
      .then(({ client_id: capturedClientId, session_id: capturedSessionId }) => {
        // sessionId is omitted (not null) when capture failed so the backend
        // falls back to its synthetic session id for the offline conversion.
        const googleClientData = capturedClientId
          ? {
              clientId: capturedClientId,
              // FB/IG in-app browsers append FBAN/FBAV blocks that push the
              // UA past the backend column (max_length 256); it used to
              // reject the whole purchase. The backend now truncates too —
              // this keeps the sent value within bounds regardless.
              userAgent: navigator.userAgent.substring(0, 256),
              ...(capturedSessionId != null && {
                sessionId: String(capturedSessionId)
              })
            }
          : undefined
        invokeDispatchSubscription(
          dispatch,
          dataSubscription,
          userEmail,
          googleClientData
        )
      })
      .catch(() => {
        // An identifier-capture failure must never block the subscription:
        // dispatch without clientId so the backend applies its own fallback.
        invokeDispatchSubscription(dispatch, dataSubscription, userEmail)
      })
  } catch (_) {
    toastError(notifications.orderPlaced)
    dispatch(enableCheckoutButton())
  }

  try {
    ReactGA.event({
      category: 'Subscription',
      action: 'offline_conversion',
      ...dataSubscription
    })
  } catch (e) {
    logger.error(
      'An error occurred while collecting GA data to track subscription start',
      {
        error: e,
        parameters: {
          actuallySubscribe: {
            dispatch,
            dataSubscription,
            userEmail,
            invokeDispatchSubscription
          }
        }
      }
    )
    dispatch(enableCheckoutButton())
  }
}

export const handleSubscription = (
  dispatch,
  dataSubscription,
  userEmail,
  usedFreeTrial,
  actuallySubscribe
  // We are disabling the rule to avoid errors in first if statement that is expecting return in "on"
  // eslint-disable-next-line consistent-return
) => {
  dispatch(disableCheckoutButton())

  if (usedFreeTrial)
    dispatch(initConfirmation(usedFreeTrialConfirmation))
      .on('accept', () =>
        actuallySubscribe(
          dispatch,
          dataSubscription,
          userEmail,
          invokeDispatchSubscription
        )
      )
      .on('reject', () => {
        dispatch(clearDiscountCode())
        return dispatch(enableCheckoutButton())
      })
  else
    return actuallySubscribe(
      dispatch,
      dataSubscription,
      userEmail,
      invokeDispatchSubscription
    )
}

export const dispatchCreditCardSubscription = async (
  dispatch,
  payload,
  plan,
  adData,
  usedFreeTrial = false,
  userEmail,
  handleSubscription
) => {
  const dataSubscription = {
    ...payload,
    plan,
    ...adData
  }
  return handleSubscription(
    dispatch,
    dataSubscription,
    userEmail,
    usedFreeTrial,
    actuallySubscribe
  )
}

export const dispatchPayPalSubscription = async (
  dispatch,
  nonce,
  order,
  plan,
  adData,
  usedFreeTrial = false,
  userEmail,
  handleSubscription
) => {
  const dataSubscription = {
    ...order,
    plan,
    payment: { paymentMethodNonce: nonce },
    ...adData
  }
  return handleSubscription(
    dispatch,
    dataSubscription,
    userEmail,
    usedFreeTrial,
    actuallySubscribe
  )
}

export const trackOrderSettlingSuccess = (initialPayload, res) => {
  try {
    const order = res.payload
    trackDVDPurchase(order)
    googleTrackConversion('order', order.cost)
    ReactPixel.trackCustom('DVD/MerchSale', {
      value: order.cost,
      currency: 'USD'
    })
  } catch (error) {
    logger.error('Error tracking settled order', {
      error,
      initialPayload,
      order: res.payload
    })
  }
}

export const dispatchSettleOrder = (
  dispatch,
  payload,
  resolveSuccessfulSettling,
  trackOrderSettlingSuccess
) => {
  dispatch(settleOrder()(payload))
    .then(([_, res]) => {
      if (res.type === types.SETTLE_ORDER_SUCCESS) {
        resolveSuccessfulSettling(dispatch, false)
        if (PROD_ENV) trackOrderSettlingSuccess(payload, res)
      } else {
        toastFailure(notifications.orderPlaced, res.payload)
        logger.error('Unsuccessful order settling', {
          initialPayload: payload,
          payload: res.payload
        })
        dispatch(enableCheckoutButton())
      }
    })
    .catch((error) => {
      toastError(notifications.orderPlaced)
      logger.error('settleOrder() error', { error, initialPayload: payload })
      dispatch(enableCheckoutButton())
    })
    .finally(() => dispatch(clearRecaptchaToken()))
}

export default (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    showFraudProtectionError() {
      toastError(notifications.fraudProtection)
    },
    loadInitialAddressData(shouldRecoverAbandonedSub) {
      let orderIsDigital
      const {
        isLoggedIn,
        cart,
        selectedAddress,
        isSubscribing,
        loggedInFromEmail
      } = stateProps
      const cartNotEmpty =
        Object.prototype.hasOwnProperty.call(cart, 'cartItems') &&
        _.size(cart.cartItems) > 0

      if (loggedInFromEmail) dispatch(getUser())

      dispatch(cartItemsFetch()).then((res) => {
        if (res[1].type === cartTypes.CART_ITEMS_FETCH_SUCCESS)
          if (
            res[1].payload.count === 0 &&
            !isSubscribing &&
            !shouldRecoverAbandonedSub
          ) {
            toastError(notifications.emptyCart)
            navigate('/cart')
          } else if (isLoggedIn || cartNotEmpty) {
            orderIsDigital = _.isUndefined(
              _.find(res[1].payload.results, { type: 'physical' })
            )
            dispatchInitAddresses(
              dispatch,
              stateProps,
              selectedAddress,
              orderIsDigital
            )
          }
      })
    },
    loadInitialPaymentData() {
      dispatch(getUserPaymentMethods()).then((res) => {
        if (res[1].type === userTypes.USERS_PAYMENT_METHODS_FETCH_SUCCESS) {
          const cards = res[1].payload.creditCards
          const paypalAccounts = res[1].payload.paypalAccounts
          const methods = [...cards, ...paypalAccounts]
          const previousMethod = stateProps.selectedPaymentMethod
          const useExistentPaymentMethod = stateProps.useExistentPaymentMethod

          if (useExistentPaymentMethod && _.isString(previousMethod))
            dispatch(selectPaymentMethod(previousMethod))
          else if (methods.length > 0) {
            let token = ''
            let isPayPal = false
            const primary = _.find(methods, { default: true })

            dispatch(useExistentPaymentMethodAction())

            if (_.has(primary, 'token')) {
              token = primary.token
              isPayPal = !_.has(primary, 'cardType')
            } else if (_.has(methods[0], 'token')) {
              token = methods[0].token
              isPayPal = !_.has(methods[0], 'cardType')
            }

            if (!_.isEmpty(token)) dispatch(selectPaymentMethod(token))

            if (isPayPal) dispatch(selectPayWithExistentPaypal(token))
          }
          dispatch(removeCheckoutCloak())
        }
      })
    },
    createNewAddress() {
      dispatch(useNewAddress())
    },
    createNewBillingAddress() {
      dispatch(useNewBillingAddress())
    },
    selectExistentAddress() {
      const addresses = stateProps.addresses
      const coupon = stateProps.validatedCode.codename

      dispatch(useExistentAddressAction())
      dispatch(cancelNewAddress())

      if (_.size(addresses) > 0) {
        const completeAddress = getCompleteAddress(addresses[0])
        const payloadToValidate = { id: completeAddress.id, coupon }

        dispatch(validateAddress(payloadToValidate))
          .then((res) => {
            if (
              res[1].type === types.VALIDATE_ADDRESS_SUCCESS &&
              res[1].payload.deliveryMethods.length > 0
            ) {
              const defaultMethod = res[1].payload.deliveryMethods[0]
              const code = defaultMethod.code
              const price = defaultMethod.cost

              dispatch(saveDeliveryMethod({ code, price }))
              dispatch(chooseAddress(completeAddress.id))
            }
          })
          .catch((payload) => toastServerError(payload.errors))
      }
    },
    selectExistentBillingAddress() {
      const addresses = stateProps.addresses
      const currentBillingAddress = stateProps.selectedBillingAddress

      dispatch(useExistentBillingAddress())

      if (!_.isNumber(currentBillingAddress) && !_.isEmpty(addresses))
        dispatch(saveBillingAddress(addresses[0].id))
    },
    chooseAddress(addressId, onUserInput = false) {
      const previousDeliveryMethod = stateProps.selectedDeliveryMethod
      const deliveryMethods = stateProps.deliveryMethods
      const coupon = stateProps.validatedCode.codename

      dispatch(chooseAddress(addressId))
      dispatch(validateAddress({ id: addressId, coupon }))
        .then((res) => {
          if (
            res[1].type === types.VALIDATE_ADDRESS_SUCCESS &&
            res[1].payload.deliveryMethods.length > 0
          ) {
            if (_.isEmpty(previousDeliveryMethod) || onUserInput) {
              const defaultMethod = res[1].payload.deliveryMethods[0]
              const code = defaultMethod.code
              const price = defaultMethod.cost

              dispatch(saveDeliveryMethod({ code, price }))
            } else {
              const method = _.find(deliveryMethods, {
                code: previousDeliveryMethod.code
              })
              dispatch(
                saveDeliveryMethod({ code: method.code, price: method.cost })
              )
            }

            dispatch(chooseAddress(addressId))
          }
        })
        .catch((payload) => toastError(payload.errors))
    },
    chooseNewAddress(data) {
      const completeAddress = getCompleteAddress(data)
      const coupon = stateProps.validatedCode.codename
      const { isMobile } = stateProps

      dispatch(validateAddress({ ...completeAddress, coupon }))
        .then((res) => {
          if (
            res[1].type === types.VALIDATE_ADDRESS_SUCCESS &&
            _.size(res[1].payload.deliveryMethods) > 0
          ) {
            const methods = _.cloneDeep(res[1].payload.deliveryMethods)
            const sortedMethods = methods.sort(
              (a, b) => parseFloat(a.cost) - parseFloat(b.cost)
            )
            const { code, cost } = sortedMethods[0]
            toastSuccess(notifications.addressValid)
            dispatch(saveNewAddress(completeAddress))
            dispatch(saveDeliveryMethod({ code, price: cost }))

            if (isMobile)
              scroller.scrollTo('deliveryMethodAnchor', {
                smooth: true,
                offset: 500
              })
          }
        })
        .catch((payload) => {
          toastServerError(payload.errors)
          dispatch(cancelNewAddress())
        })
    },
    chooseDeliveryMethod(code, price) {
      dispatch(saveDeliveryMethod({ code, price }))
    },
    cancelChooseNewAddress() {
      dispatch(cancelNewAddress())
    },
    selectShippingInfo(selectedBillingAddress = {}) {
      dispatch(completeShippingInfo())
      dispatch(showNewPaymentMethodForm())
      if (!_.isEmpty(selectedBillingAddress)) navigate('/review_order')
    },
    getBraintreeClientToken: () => dispatchGetUserBraintreeToken(dispatch),
    savePaymentMethodCheckout(tokenizer, options, deviceData) {
      return tokenizeCheckout(
        dispatch,
        tokenizer,
        options,
        deviceData,
        stateProps.recaptcha
      )
    },
    toggleBillToShippingAddress: () => {
      dispatch(toggleBillToShippingAddressAction())
      // AddressForm now handles initial values through props
    },
    chooseExistentPaymentMethod: (token) => {
      const { paymentMethodOptions } = stateProps
      const method = _.find(paymentMethodOptions, { token })
      const isPayPal = !_.has(method, 'cardType')

      if (isPayPal) dispatch(selectPayWithExistentPaypal())
      else dispatch(selectPayWithCreditCard())

      dispatch(selectPaymentMethod(token))
    },
    selectExistentPaymentMethod: () =>
      dispatch(useExistentPaymentMethodAction()),
    createNewPaymentMethod: () => dispatch(useNewPaymentMethod()),
    chooseBillingAddress(addressId) {
      dispatch(saveBillingAddress(addressId))
    },
    chooseNewBillingAddress(billingAddress) {
      if (!_.isEmpty(billingAddress)) {
        const newData = {
          ...billingAddress,
          countryCodeAlpha2: getCountryCode(billingAddress.countryName)
        }
        const {
          tokenizer,
          storeMethod,
          payWithCreditCard,
          useExistentPaymentMethod
        } = stateProps

        // Check if user already has a payment method selected
        const hasSelectedPaymentMethod = !_.isNull(stateProps.selectedPaymentMethod)

        // Save billing address before navigating
        dispatch(saveBillingAddress(newData))

        // Only tokenize if using credit card AND not using existing payment method
        // AND no payment method is already selected
        if (payWithCreditCard && !useExistentPaymentMethod && !hasSelectedPaymentMethod)
          // Collect Braintree device data before tokenizing — a card saved
          // without it is indistinguishable from carding traffic at the edge.
          // On collector failure the submit is blocked with a visible error
          // instead of proceeding without the blob.
          addFraudProtection(
            (deviceData) =>
              tokenize(
                dispatch,
                tokenizer,
                { storeMethod },
                deviceData,
                stateProps.recaptcha
              ),
            [],
            stateProps.clientToken,
            () => toastError(notifications.fraudProtection)
          )
        // Use setTimeout to ensure Redux state has fully propagated
        // before navigation, so RequireOrderReady guard sees updated state
        else setTimeout(() => navigate('/review_order'), 0)
      }
    },
    cancelChooseBillingAddress() {
      // AddressForm now handles initial values through props
    },
    initializeShippingInfo() {
      dispatch(initializeShippingInfoAction())
      // AddressForm now handles initial values through props
    },
    initializeBillingInfo() {
      // AddressForm now handles initial values through props
    },
    clearNewPayment() {
      dispatch(clearSelectedPayment())
    },
    toggleDiscountCode() {
      dispatch(toggleDiscountCode())
    },
    cartChanged() {
      const { planSelected, validatedCode } = stateProps
      const planId = _.has(planSelected, 'id') ? planSelected.id : null

      toastSuccess(notifications.cartUpdated)
      validateCoupon(
        dispatch,
        notifications.couponNoLongerValid,
        validatedCode,
        planId
      )
    },
    validateCoupon() {
      const { planSelected, validatedCode } = stateProps
      const planId = _.has(planSelected, 'id') ? planSelected.id : null

      validateCoupon(
        dispatch,
        notifications.discountCodeValid,
        validatedCode,
        planId
      )
    },
    setCheckoutCloak() {
      dispatch(cloakCheckout())
    },
    removeCloakCheckout() {
      dispatch(removeCheckoutCloak())
    },
    setPendingSubscription(plan) {
      dispatch(isSubscriptionPending()).then((res) => {
        if (
          res[1].payload.status === 404 ||
          (_.has(res[1], 'payload.plan') && res[1].payload.plan !== plan)
        )
          dispatch(markSubscriptionPending({ plan }))
      })
    },
    loginFromEmail(query, isLoggedIn) {
      const { token } = query  
      if (!isLoggedIn) dispatch(loginFromEmailAction(token))
    },
    getPendingSubscription() {
      dispatch(isSubscriptionPending()).then((res) => {
        if (
          res[1].type === subscriptionTypes.IS_SUBSCRIPTION_PENDING_SUCCESS &&
          _.has(res[1], 'payload.plan')
        ) {
          const id = res[1].payload.plan
          dispatch(subscriptionPlansFetch()).then((res) => {
            const plans = res[1].payload.results
            dispatch(activateSubscribingMode(_.find(plans, { id })))
          })
        }
      })
    },
    placeSubscription: (payload, plan, usedFreeTrial, userEmail) => {
      getUTMDataFromCookies(dispatch).then((rawUTMdata) => {
        dispatchCreditCardSubscription(
          dispatch,
          payload,
          plan,
          getAdDataObjectFrom(rawUTMdata),
          usedFreeTrial,
          userEmail,
          handleSubscription
        )
      })
    },
    placePayPalSubscription: (nonce, order, plan, usedFreeTrial, userEmail) => {
      getUTMDataFromCookies(dispatch).then((rawUTMdata) => {
        dispatchPayPalSubscription(
          dispatch,
          nonce,
          order,
          plan,
          getAdDataObjectFrom(rawUTMdata),
          usedFreeTrial,
          userEmail,
          handleSubscription
        )
      })
    },
    placeOrder: (payload) =>
      dispatchSettleOrder(
        dispatch,
        payload,
        resolveSuccessfulSettling,
        trackOrderSettlingSuccess
      ),
    placePayPalOrder: (nonce, order, deviceData) =>
      dispatchSettleOrder(
        dispatch,
        {
          ...order,
          deviceData,
          payment: { paymentMethodNonce: nonce }
        },
        resolveSuccessfulSettling,
        trackOrderSettlingSuccess
      ),
    fetchLifetimePreview: (planId, coupon) =>
      dispatch(fetchLifetimePreview(planId, coupon)),
    applyLifetimeCoupon: (code) => dispatch(applyLifetimeCoupon(code)),
    clearLifetimeCoupon: () => dispatch(clearLifetimeCoupon()),
    purchaseLifetime: (payload) => dispatch(purchaseLifetime(payload)),
    placePayPalLifetime: (nonce, _order, deviceData) =>
      dispatch(
        purchaseLifetime({
          newPlan: stateProps.planSelected && stateProps.planSelected.id,
          paymentMethodNonce: nonce,
          coupon:
            stateProps.validatedCode && stateProps.validatedCode.codename,
          deviceData
        })
      ),
    // SPEC-063: the promo itself lives in the cookie / app state; what the
    // checkout carries is derived per plan from it. A zero entry means the
    // code must not travel with this purchase (no banner, no coupon on the
    // subscribe or change_plan request), and picking a discountable plan next
    // re-derives it from the same source — so nothing is lost by comparing.
    applyPromo(plan, globalPromo) {
      getGlobalPromoCookie(dispatch).then((data) => {
        const promoCookie = data.value
          ? JSON.parse(data.value)
          : { codename: '' }
        const promocode = globalPromo.codename || promoCookie.codename
        if (!promocode) return
        const discount = promoDiscountForPlan(globalPromo, plan)
        if (discount > 0)
          dispatch(saveValidatedCode({ ...globalPromo, discount }))
        else dispatch(clearDiscountCode())
      })
    },
    ...dispatchClearCheckout(dispatch),
    ...innerNavHandlers(dispatch),
    ...bannerHandler(dispatch)
  }
}

export const reviewOrderDispatchers = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    showFraudProtectionError() {
      toastError(notifications.fraudProtection)
    },
    getBraintreeClientToken: () => dispatchGetUserBraintreeToken(dispatch),
    placeOrder: (payload) =>
      dispatchSettleOrder(
        dispatch,
        payload,
        resolveSuccessfulSettling,
        trackOrderSettlingSuccess
      ),
    placePayPalOrder: (nonce, order, deviceData) =>
      dispatchSettleOrder(
        dispatch,
        {
          ...order,
          deviceData,
          payment: {
            paymentMethodNonce: nonce
          }
        },
        resolveSuccessfulSettling,
        trackOrderSettlingSuccess
      ),
    cancelCompleteShippingInfo() {
      dispatch(initializeShippingInfoAction())
      // AddressForm now handles initial values through props
      navigate('/checkout')
    },
    cancelCompleteBillingInfo(_, useExistentPaymentMethod, payWithCreditCard) {
      // AddressForm now handles initial values through props
      if (!useExistentPaymentMethod && payWithCreditCard)
        dispatch(clearSelectedPayment())
      navigate('/checkout')
    },
    placeSubscription: (payload, plan, usedFreeTrial, userEmail) => {
      getUTMDataFromCookies(dispatch).then((rawUTMdata) => {
        exports.dispatchCreditCardSubscription(
          dispatch,
          payload,
          plan,
          getAdDataObjectFrom(rawUTMdata),
          usedFreeTrial,
          userEmail,
          exports.handleSubscription
        )
      })
    },
    placePayPalSubscription: (nonce, order, plan, usedFreeTrial, userEmail) => {
      getUTMDataFromCookies(dispatch).then((rawUTMdata) => {
        exports.dispatchPayPalSubscription(
          dispatch,
          nonce,
          order,
          plan,
          getAdDataObjectFrom(rawUTMdata),
          usedFreeTrial,
          userEmail,
          exports.handleSubscription
        )
      })
    },
    ...dispatchClearCheckout(dispatch),
    ...innerNavHandlers(dispatch)
  }
}

export const thankYouDispatchers = (dispatch) => ({
  goToWalkthrough() {
    navigate('/walkthrough')
  },
  goHome() {
    navigate('/')
  },
  updateInterests(data) {
    dispatch(updateUserProfile(data))
  }
})
